import { ActionButton } from '@/components/common/ActionButton';
import { TOOL_MANAGEMENT_DIALOG_PT } from '@/components/dialogs/dialogTheme';
import { Warning } from '@phosphor-icons/react';
import { Dialog } from 'primereact/dialog';
import type { FC } from 'react';
import { useTranslation } from 'react-i18next';

interface ConfirmDeleteDialogProps {
  visible: boolean;
  title: string;
  message: string;
  isDeleting?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}

/**
 * The one "are you sure" in the app — projects, threads, tools and two panels all open this.
 *
 * Red is spent twice and only twice: on the warning tile and on the button that does the thing.
 * The cancel is a ghost, so the quiet way out is the one that looks like nothing is at stake.
 *
 * The old full-screen toggle is gone. It came from the image and JSON viewers this dialog was
 * copied from, where a bigger window shows more; a one-sentence question has nothing to show at
 * 100 vw, and the control sat where the close button was expected.
 */
export const ConfirmDeleteDialog: FC<ConfirmDeleteDialogProps> = ({
  visible,
  title,
  message,
  isDeleting = false,
  onConfirm,
  onCancel,
}) => {
  const { t } = useTranslation();

  const footer = (
    <>
      <ActionButton variant="ghost" onClick={onCancel} disabled={isDeleting}>
        {t('common.cancel')}
      </ActionButton>
      <ActionButton variant="danger" onClick={onConfirm} loading={isDeleting}>
        {t('common.delete')}
      </ActionButton>
    </>
  );

  return (
    <Dialog
      header={title}
      visible={visible}
      // A delete in flight cannot be taken back, so the dialog stays put until it resolves.
      onHide={() => {
        if (!isDeleting) onCancel();
      }}
      closable={!isDeleting}
      modal={true}
      draggable={false}
      resizable={false}
      className="w-full max-w-md"
      footer={footer}
      pt={TOOL_MANAGEMENT_DIALOG_PT}
    >
      <div className="flex items-start gap-4">
        <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-red-50 text-red-600">
          <Warning size={20} aria-hidden="true" />
        </span>
        <p className="m-0 text-sm text-gray-600 [text-wrap:pretty]">{message}</p>
      </div>
    </Dialog>
  );
};
